import { getRelatedPoiIndices } from 'app/modules/wallforming/components/scenes/objects/pois/utils/poiUtils';
import { placeBufferedArticleGroups } from 'app/modules/wallforming/components/state-management/placement/slices/connectionSlice';

import type { PoiSliceState } from 'app/modules/wallforming/components/state-management/placement/states/poiSliceState';
import type { PlacementState } from 'app/modules/wallforming/components/state-management/placement/types';
import type { StateCreator } from 'zustand';

export const createPoiSlice: StateCreator<PlacementState, [], [], PoiSliceState> = (set, get) => ({
    pois: [],
    setPois: (pois) => {
        set((state) => {
            if (pois.length === 0) {
                return { pois: [], isPlacementDataInitialized: false };
            }

            const poiGuidToIndex = new Map<string, number>();
            const rebuiltArticleMapByPoiIndex = new Map<number, Record<string, string[]>>();

            pois.forEach((poiData, index) => {
                poiGuidToIndex.set(poiData.poi.poiGuid, index);
                rebuiltArticleMapByPoiIndex.set(index, {});
            });

            Object.entries(state.articles).forEach(([articleId, article]) => {
                Object.entries(article.articleInstances).forEach(([instanceId, instance]) => {
                    if (!instance.poiGuids?.length) {
                        return;
                    }

                    instance.poiGuids.forEach((poiGuid) => {
                        const poiIndex = poiGuidToIndex.get(poiGuid);
                        if (poiIndex === undefined) {
                            return;
                        }

                        getRelatedPoiIndices(pois, poiIndex).forEach((relatedPoiIndex) => {
                            const poiArticleMap = rebuiltArticleMapByPoiIndex.get(relatedPoiIndex)!;
                            const existingIds = poiArticleMap[articleId] ?? [];
                            if (!existingIds.includes(instanceId)) {
                                poiArticleMap[articleId] = [...existingIds, instanceId];
                            }
                        });
                    });
                });
            });

            const nextPois = pois.map((poiData, index) => ({
                ...poiData,
                articles: rebuiltArticleMapByPoiIndex.get(index) ?? {}
            }));

            return { pois: nextPois, isPlacementDataInitialized: true };
        });

        // Handle any article group placement that was received before POIs were loaded (e.g. RequestArticleStateAsync)
        placeBufferedArticleGroups(set, get);
    },

    addArticleToPoi: (poiIndex, articleId, articleInstanceId) => {
        set((state) => {
            const existingPoi = state.pois[poiIndex];
            if (!existingPoi) {
                return state;
            }

            const existingArticles = existingPoi.articles[articleId] ?? [];
            if (existingArticles.includes(articleInstanceId)) {
                return state;
            }

            const updatedPoi = {
                ...existingPoi,
                articles: { ...existingPoi.articles, [articleId]: [...existingArticles, articleInstanceId] }
            };

            const newPois = [...state.pois];
            newPois[poiIndex] = updatedPoi;
            return { pois: newPois };
        });
    },

    setPoiAutoFormworkPlaced: (poiIndex, placed) => {
        set((state) => {
            const existingPoi = state.pois[poiIndex];
            if (!existingPoi) {
                return state;
            }

            const updatedPoi = { ...existingPoi, autoFormworkPlaced: placed };

            const newPois = [...state.pois];
            newPois[poiIndex] = updatedPoi;
            return { pois: newPois };
        });
    },

    removeArticleFromPoi: (articleId, articleInstanceId) => {
        set((state) => {
            const newPois = state.pois.map((poiData) => {
                const existingInstances = poiData.articles[articleId] ?? [];
                if (!existingInstances.includes(articleInstanceId)) {
                    return poiData;
                }
                const updatedArticleMap = { ...poiData.articles };
                const updatedInstances = existingInstances.filter((id) => id !== articleInstanceId);
                if (updatedInstances.length > 0) {
                    updatedArticleMap[articleId] = updatedInstances;
                } else {
                    delete updatedArticleMap[articleId];
                }
                return {
                    ...poiData,
                    articles: updatedArticleMap,
                    autoFormworkPlaced:
                        poiData.autoFormworkPlaced && Object.keys(updatedArticleMap).length === 0
                            ? false
                            : poiData.autoFormworkPlaced
                };
            });
            return { pois: newPois };
        });
    },

    removeArticlesFromPois: (removed) => {
        set((state) => {
            if (removed.length === 0) {
                return state;
            }

            // create instanceToPoi lookup: instanceId → { poiIndex, articleId }
            const instanceToPois = new Map<string, Array<{ poiIndex: number; articleId: string }>>();
            state.pois.forEach((poiData, poiIndex) => {
                Object.entries(poiData.articles).forEach(([articleId, instanceIds]) => {
                    instanceIds.forEach((id) => {
                        const memberships = instanceToPois.get(id) ?? [];
                        memberships.push({ poiIndex, articleId });
                        instanceToPois.set(id, memberships);
                    });
                });
            });

            // group removed instances by article and by poi to minimize the number of state updates
            const groupedByPoi = new Map<number, Map<string, Set<string>>>();
            removed.forEach(({ articleId, articleInstanceId }) => {
                const memberships = instanceToPois.get(articleInstanceId);
                if (!memberships) {
                    return;
                }
                memberships.forEach(({ poiIndex }) => {
                    if (!groupedByPoi.has(poiIndex)) {
                        groupedByPoi.set(poiIndex, new Map());
                    }
                    const groupedByArticle = groupedByPoi.get(poiIndex)!;
                    if (!groupedByArticle.has(articleId)) {
                        groupedByArticle.set(articleId, new Set());
                    }
                    groupedByArticle.get(articleId)!.add(articleInstanceId);
                });
            });

            if (groupedByPoi.size === 0) {
                return state;
            }

            const updatedPois = [...state.pois];
            groupedByPoi.forEach((groupedByArticle, poiIndex) => {
                const poi = updatedPois[poiIndex];
                const updatedArticleMap = { ...poi.articles };

                groupedByArticle.forEach((idsToRemove, articleId) => {
                    const remaining = (updatedArticleMap[articleId] ?? []).filter((id) => !idsToRemove.has(id));
                    if (remaining.length > 0) {
                        updatedArticleMap[articleId] = remaining;
                    } else {
                        delete updatedArticleMap[articleId];
                    }
                });

                updatedPois[poiIndex] =
                    poi.autoFormworkPlaced && Object.keys(updatedArticleMap).length === 0
                        ? { ...poi, articles: updatedArticleMap, autoFormworkPlaced: false }
                        : { ...poi, articles: updatedArticleMap };
            });

            // Clean up applied solutions cache
            const appliedCornerPlacementSolutionByPoi = { ...state.appliedCornerPlacementSolutionByPoi };
            groupedByPoi.forEach((_groupedByArticle, poiIndex) => {
                appliedCornerPlacementSolutionByPoi[poiIndex] = null;
            });

            return { pois: updatedPois, appliedCornerPlacementSolutionByPoi };
        });
    }
});
